import type { ReactNode } from 'react';
import {
  transformDestinationEvent,
  type DestinationEvent,
} from '@eventbrite/consumer-search.event-renderer';
import { BoltIcon } from '@eventbrite/marmalade';
import { defineMessages, useIntl } from 'react-intl';

import { useSavedEventIds } from '@/hooks/useSavedEventIds';

import type { AuthState } from '../_shared/EventsBucket/EventsBucket';
import { OrganizerHorizontalEventCard } from '../_shared/OrganizerHorizontalEventCard/OrganizerHorizontalEventCard';
import styles from './FeaturedEvent.module.scss';

const messages = defineMessages({
  highlight: {
    id: 'HIGHLIGHT',
    defaultMessage: 'HIGHLIGHT',
  },
});

interface Props {
  event: DestinationEvent;
  locale: string;
  organizerId: string;
  authState: AuthState;
  action?: ReactNode;
}

export function HighlightedEventCard({ event, locale, organizerId, authState, action }: Props) {
  const intl = useIntl();
  const saveLookupEnabled = authState === 'authenticated';
  const { savedEventIds, isSaveStateReady } = useSavedEventIds(
    organizerId,
    [event.id],
    saveLookupEnabled,
  );
  const eventWithSaveState = savedEventIds.has(event.id)
    ? { ...event, saves: { saved_by_you: true } }
    : event;
  const formattedEvent = transformDestinationEvent(eventWithSaveState);
  const showSaveAction = authState !== 'unknown' && (authState === 'anonymous' || isSaveStateReady);

  return (
    <section
      className={styles.highlightSection}
      aria-label={intl.formatMessage(messages.highlight)}
    >
      <div className={styles.highlightCard}>
        <OrganizerHorizontalEventCard
          event={formattedEvent}
          locale={locale}
          statsigLocationString="Organizer Profile - highlighted-event"
          bucketName="highlighted-event"
          authState={authState}
          showSaveAction={showSaveAction}
          showShareAction={authState !== 'unknown'}
          moreActions={action}
          variant="highlighted"
          leadingContent={
            <div className={styles.highlightLabel}>
              <BoltIcon size="xsmall" aria-hidden />
              <span>{intl.formatMessage(messages.highlight)}</span>
            </div>
          }
        />
      </div>
    </section>
  );
}
